import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  SimpleGrid,
  Divider
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { FAST_API_BASE_SHARING_LINKS } from './config';
import hhlMark from './hoggedin-icon-white.png';
import CompaniesShowcase from './components/CompaniesShowcase';
import FormattedText from './components/hoggedin/FormattedText';
import KeySkills from './components/public/KeySkills';
import ProfileNameHeading from './components/public/ProfileNameHeading';
import SectionTitle, { BRAND_BLUE } from './components/public/SectionTitle';

const GENERIC_ERROR_MESSAGE = 'This link is invalid or has expired.';
const MAX_VISIBLE_COMPANIES = 5;
const DISPLAY_FONT = "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const BODY_FONT = "'Manrope', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const DESKTOP_PROFILE_GRID_COLUMNS = 'minmax(0, 420px) minmax(0, 1fr)';
// Both columns start with a header of the same height (the "in" mark on the left, the
// hedgehog lab wordmark on the right) followed by the same gap, so the logos' tops line up
// and the name lines up with the first section title.
const LOGO_HEIGHT = '68px';
// Two lines ("hedgehog" / "lab") at line-height 1, so 2 x 34px = the 68px logo height.
const WORDMARK_FONT_SIZE = '34px';
const HEADER_GAP = 10;
const PRINT_PROFILE_GRID_COLUMNS = '34% 66%';

const toProperCase = (value = '') =>
  value
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const HoggedInPublicPage = () => {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSharedProfile = async () => {
      if (!token) {
        setError(GENERIC_ERROR_MESSAGE);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${FAST_API_BASE_SHARING_LINKS}/${encodeURIComponent(token)}`);
        if (!response.ok) {
          throw new Error(GENERIC_ERROR_MESSAGE);
        }

        const data = await response.json();
        if (!cancelled) {
          setPayload(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(GENERIC_ERROR_MESSAGE);
          setPayload(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSharedProfile();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const person = payload?.person ?? null;
  const expiresAt = payload?.expires_at ?? null;

  const formattedExpiresAt = useMemo(() => {
    if (!expiresAt) {
      return 'Unknown';
    }
    const parsed = new Date(expiresAt);
    if (Number.isNaN(parsed.getTime())) {
      return expiresAt;
    }
    return parsed.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }, [expiresAt]);

  const normalizedSkills = useMemo(() => {
    if (!Array.isArray(person?.skills)) {
      return [];
    }
    return person.skills
      .map((skill) => {
        if (!skill || typeof skill !== 'object') {
          return null;
        }
        const skillid = typeof skill.skillid === 'number' ? skill.skillid : Number(skill.skillid);
        const skillname = typeof skill.skillname === 'string' ? skill.skillname : '';

        if (Number.isNaN(skillid) || skillid == null || skillname.length === 0) {
          return null;
        }

        return { id: String(skillid), label: skillname, level: skill.level ?? null };
      })
      .filter(Boolean);
  }, [person]);

  const displayName = useMemo(() => {
    const name = person?.name;
    if (typeof name !== 'string') {
      return name ?? '';
    }
    return toProperCase(name);
  }, [person?.name]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Spinner size="xl" color="blue.500" thickness="4px" speed="0.6s" />
      </Flex>
    );
  }

  if (error || !person) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50" px={4}>
        <Box bg="white" p={8} borderRadius="lg" boxShadow="md" maxW="lg" textAlign="center">
          <Heading as="h2" size="md" mb={4}>
            {GENERIC_ERROR_MESSAGE}
          </Heading>
          <Text color="gray.600">Please contact support for a new link.</Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      bg="#F0F3F7"
      minH="100vh"
      py={{ base: 6, md: 10 }}
      px={{ base: 4, md: 5 }}
      justify="center"
      align="flex-start"
      fontFamily={BODY_FONT}
      sx={{
        '@media print': {
          background: 'white',
          minHeight: 'auto',
          padding: '40px 20px'
        }
      }}
    >
      <SimpleGrid
        w="full"
        maxW="1000px"
        columns={{ base: 1, md: 2 }}
        templateColumns={{ base: '1fr', md: DESKTOP_PROFILE_GRID_COLUMNS }}
        bg="white"
        boxShadow="0 8px 24px rgba(0, 0, 0, 0.08)"
        borderRadius="8px"
        overflow="hidden"
        sx={{
          '@media print': {
            gridTemplateColumns: PRINT_PROFILE_GRID_COLUMNS,
            boxShadow: 'none'
          }
        }}
      >
        <Box
          bg="#1885F2"
          color="white"
          p={{ base: 8, md: 10 }}
          display="flex"
          flexDirection="column"
          gap={HEADER_GAP}
          sx={{
            '@media print': {
              // Top/bottom match the right column so the headers stay aligned.
              padding: '40px 16px 40px 24px'
            }
          }}
        >
          <Box h={LOGO_HEIGHT} data-testid="public-header-left">
            <Box as="img" src={hhlMark} alt="hedgehog lab mark" h="100%" w="auto" objectFit="contain" />
          </Box>

          <Box>
            <ProfileNameHeading name={displayName} role={person?.role} font={DISPLAY_FONT} />
            {/* KeySkills spaces its divider evenly between the role and the skills. */}
            <KeySkills skills={normalizedSkills} headingFont={DISPLAY_FONT} />
          </Box>
        </Box>

        <Box
          bg="white"
          color="#0B151F"
          p={{ base: 8, md: 10 }}
          display="flex"
          flexDirection="column"
          gap={HEADER_GAP}
          sx={{
            '@media print': {
              padding: '40px 40px 40px 24px'
            }
          }}
        >
          <Flex h={LOGO_HEIGHT} justify="flex-end" align="flex-start" data-testid="public-header-right">
            <Box
              display="flex"
              flexDirection="column"
              fontFamily={DISPLAY_FONT}
              fontSize={WORDMARK_FONT_SIZE}
              fontWeight="800"
              lineHeight="1"
              letterSpacing="0"
            >
              <Text as="span" color="#0B151F">
                hedgehog
              </Text>
              <Text as="span" color={BRAND_BLUE}>
                lab
              </Text>
            </Box>
          </Flex>

          <Box display="flex" flexDirection="column" gap={8}>
          {person.description && (
            <Box>
              <SectionTitle font={DISPLAY_FONT}>Profile Summary</SectionTitle>
              <FormattedText
                text={person.description}
                fontSize="14px"
                lineHeight="1.6"
                color="#0B151F"
                headingProps={{ fontFamily: DISPLAY_FONT }}
              />
            </Box>
          )}

          <Box>
            <SectionTitle font={DISPLAY_FONT}>Experience</SectionTitle>
            <FormattedText
              text={person.experience?.trim() ? person.experience : 'Experience details unavailable.'}
              fontSize="14px"
              lineHeight="1.6"
              color="#0B151F"
              headingProps={{ fontFamily: DISPLAY_FONT }}
            />
          </Box>

          <CompaniesShowcase
            companies={person?.companies}
            heading="Companies"
            maxVisible={MAX_VISIBLE_COMPANIES}
            imageHeight={{ base: '48px', sm: '60px', md: '70px' }}
            showDescriptions
            containerProps={{ bg: 'white', boxShadow: 'none', p: 0 }}
            renderHeading={(heading) => <SectionTitle font={DISPLAY_FONT}>{heading}</SectionTitle>}
            gridProps={{
              minChildWidth: { base: '120px', sm: '140px', md: '160px' },
              spacing: { base: 4, md: 6 }
            }}
          />

          <Box sx={{ '@media print': { display: 'none' } }}>
            <Divider borderColor="#DFE5EB" mb={2} />
            <Text color="#757F8A" fontSize="12px">
              This page expires at {formattedExpiresAt}
            </Text>
          </Box>
          </Box>
        </Box>
      </SimpleGrid>
    </Flex>
  );
};

export default HoggedInPublicPage;
